<template>
  <div class="anonymous-concern-form">
    <header class="anonymous-concern-form__header">
      <img
        v-if="school.logoUrl"
        :src="school.logoUrl"
        :alt="`${school.name} logo`"
        class="anonymous-concern-form__logo"
      />
      <h1>{{ school.name }}</h1>
      <p class="anonymous-concern-form__subtitle">{{ t('anonymous_concerns.new.title') }}</p>
    </header>

    <div v-if="submitted" class="alert alert-success anonymous-concern-form__confirmation" role="status" data-test="confirmation">
      {{ t('anonymous_concerns.form.confirmation') }}
    </div>

    <form v-else novalidate @submit.prevent="onSubmit">
      <div v-if="serverErrors.length" class="alert alert-danger" role="alert" data-test="server-errors">
        <ul class="anonymous-concern-form__errors">
          <li v-for="(error, index) in serverErrors" :key="index">{{ error }}</li>
        </ul>
      </div>

      <div class="form-group">
        <label for="anonymous-concern-name">{{ t('anonymous_concerns.form.name_label') }}</label>
        <input
          id="anonymous-concern-name"
          v-model="name"
          type="text"
          class="form-control"
        />
      </div>

      <div class="form-group" :class="{ 'has-error': textError }">
        <label for="anonymous-concern-text">
          {{ t('anonymous_concerns.form.text_label') }}<span class="anonymous-concern-form__required" aria-hidden="true">*</span>
        </label>
        <textarea
          id="anonymous-concern-text"
          v-model="text"
          class="form-control"
          rows="6"
          required
          aria-required="true"
          :aria-invalid="textError ? 'true' : null"
          :aria-describedby="textError ? 'anonymous-concern-text-error' : null"
        ></textarea>
        <span v-if="textError" id="anonymous-concern-text-error" class="help-block" data-test="text-error">{{ textError }}</span>
      </div>

      <f-button
        type="primary-alt"
        kind="submit"
        :text="t('anonymous_concerns.form.submit')"
        :disabled="submitting"
      />
    </form>

    <section class="anonymous-concern-form__safeguarding" aria-label="Safeguarding contacts">
      <h2 class="anonymous-concern-form__safeguarding-heading">{{ t('anonymous_concerns.form.safeguarding_heading') }}</h2>
      <div class="anonymous-concern-form__routes">
        <div v-for="(route, index) in safeguarding.contacts" :key="index" class="anonymous-concern-form__route">
          <p class="anonymous-concern-form__route-name">
            <strong>{{ route.name }}<span v-if="route.phone"> - {{ route.phone }}</span></strong>
          </p>
          <p v-if="route.description" class="anonymous-concern-form__route-detail">{{ route.description }}</p>
        </div>
      </div>
      <p class="anonymous-concern-form__emergency">
        {{ t('anonymous_concerns.safeguarding.emergency_label') }} {{ safeguarding.emergency.phone }}
      </p>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { csrf } from '~/helpers/application_helpers.js';

const { t } = I18n;

const props = defineProps({
  qrUuid: String,
  submitUrl: String,
  school: Object,
  safeguarding: Object,
});

const text = ref('');
const name = ref('');

const textError = ref('');
const serverErrors = ref([]);

const submitting = ref(false);
const submitted = ref(false);

const validate = () => {
  textError.value = '';

  if (!text.value.trim()) {
    textError.value = t('anonymous_concerns.form.errors.text_required');
  }

  return !textError.value;
};

const onSubmit = async () => {
  if (submitting.value) return;
  if (!validate()) return;

  submitting.value = true;
  serverErrors.value = [];

  try {
    const response = await fetch(props.submitUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrf(),
      },
      body: JSON.stringify({
        text: text.value,
        name: name.value,
        qr: props.qrUuid,
      }),
    });

    if (response.status === 201) {
      submitted.value = true;
      return;
    }

    const payload = await response.json().catch(() => ({}));
    serverErrors.value = payload.errors || [t('anonymous_concerns.form.errors.generic')];
    submitting.value = false;
  } catch {
    serverErrors.value = [t('anonymous_concerns.form.errors.generic')];
    submitting.value = false;
  }
};
</script>

<style lang="scss" scoped>
.anonymous-concern-form {
  max-width: 640px;
  margin: 0 auto;
  padding: 1.5rem 1rem;

  &__header {
    text-align: center;
    margin-bottom: 1.5rem;
  }

  &__logo {
    max-height: 96px;
    max-width: 100%;
    margin-bottom: 0.5rem;
  }

  &__subtitle {
    margin: 0.25rem 0 0;
    color: #6c757d;
    font-size: 1rem;
  }

  &__confirmation {
    margin: 1.5rem 0;
    padding: 1.5rem;
  }

  .form-group {
    margin-bottom: 1.75rem;
  }

  label {
    display: inline-block;
    margin-bottom: 0.5rem;
  }

  .form-control {
    padding: 0.625rem 0.75rem;
  }

  &__required {
    margin-left: 0.2rem;
    color: #c3252c;
  }

  &__errors {
    margin: 0;
    padding-left: 1.25rem;
  }

  &__safeguarding {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #ddd;
  }

  &__safeguarding-heading {
    margin: 0 0 0.5rem;
    font-size: 1rem;
    font-weight: 700;
  }

  &__routes {
    padding: 0.875rem 1rem;
    background: #f4f5f7;
    border-radius: 8px;
  }

  &__route + &__route {
    margin-top: 1rem;
  }

  &__route-name {
    margin: 0;
  }

  &__route-detail {
    margin: 0.125rem 0 0;
    color: #5f6b7a;
    font-size: 0.9rem;
    line-height: 1.4;
  }

  &__emergency {
    margin: 1rem 0 0;
    padding: 0.75rem 1rem;
    background: #fdeaea;
    border-radius: 8px;
    color: #b3261e;
    font-weight: 600;
    text-align: center;
  }
}
</style>
